import i18n from "src/i18n"
import { getPlace } from "src/utils/maps"
import { raceNames } from "src/utils/races"
import { foodIcon } from "src/utils/resources"

/* What a round's events say to the player who lived them.

   Kept out of the component for the reason `store/game/selectors.test.js` gives: anything importing
   a store barrel drags in the whole app graph, and this is the part worth testing on its own. */

// "3 Zombies, 1 Cursed" from {zombie: 3, cursed: 1} — what the missile actually hit, in words.
const unitBreakdown = (byUnit) =>
  Object.entries(byUnit || {})
    .filter(([, dead]) => dead > 0)
    .map(([unit, dead]) => `${dead} ${i18n.t(`city.units.${unit}`, { defaultValue: unit })}`)
    .join(", ")

/* What the horde ate (owner's U5, 2026-09-07): the meat a battle's kills paid this player, said
   after the line. Only ever non-zero for a zombie — the server reports it per killer, and a human
   is never one — so a human's line is left exactly as it was. */
const withMeat = (text, meat) => (meat > 0 ? `${text} ${i18n.t("game.events.meatWon", { meat })}` : text)

const withBreakdown = (text, byUnit) => {
  const detail = unitBreakdown(byUnit)
  return detail ? `${text} (${detail})` : text
}

const place = (column, row) => {
  const { name } = getPlace({ column, row })
  return name ? i18n.t("game.events.place", { name, column, row }) : `${column}-${row}`
}

/* Types the report never prints at all (owner, 2026-09-09). Both are still sent, and both are
   still read elsewhere — this is about what earns a line in the report, not about what the server
   says:

   - `enemy_sighted`. "menyanyo planted 198 troops at 103-16 — they came from Leeuwarden" is a
     sentence about a march the map is already drawing, under a red ring the map puts on the cell
     for the round. The ring stays (`utils/mapBoard` reads the event straight off the metadata);
     the paragraph restating it goes.
   - `march_halted`. A march stopped in a fight *is* a fight, and the battle it caused has its own
     line one row down. Two lines about one event, and the second one has the numbers.

   Kept as a list rather than deleted from `describeRoundEvent`, because the wording is what a
   type means and the report is only one of its readers; a type that stops being printed here has
   not stopped happening. */
const NEVER_REPORTED = new Set(["enemy_sighted", "march_halted"])

/* The events a report actually prints (owner's S22b, 2026-09-05). Two kinds go:

   - a `cell_lost` that is neither ours nor taken by us — "X is ownerless now" is the other
     side's news told as ours. The one who took it keeps a line (owner, 2026-10-04): a capture
     moves no deed since Q16, so there is no capture line to stand in for it;
   - a `battle` at a site whose `battle_ended` is in the same round — the closing line carries the
     whole story (rounds, result) and the per-round line beside it said the same thing twice;
   - a `cell_lost` at a site whose `battle_ended` is in the same round (owner, 2026-09-07): a city
     lost in battle is what "the battle at X is over — defeat" already says, and "You lost X" under
     it was the same news twice. A city lost with no battle fought — walked into empty — keeps
     its line, since then it is the only word.

   Both panels — the dock's report and the round digest — read their lists through this, so a rule
   about what is worth a line lives once. */
export const collapseRoundEvents = (events) =>
  (events || []).filter((event) => {
    if (NEVER_REPORTED.has(event.type)) return false
    if (event.type === "cell_lost" && !event.lostByUs && !event.takenByUs) return false
    const battleEndedHere = () =>
      events.some(
        (other) => other.type === "battle_ended" && other.column === event.column && other.row === event.row
      )
    if ((event.type === "battle" || event.type === "cell_lost") && battleEndedHere()) return false
    return true
  })

/* The report's order (owner, 2026-10-07: "sorted by user. First the own events for the user, then
   the allies sorted by player, and then, by tipology. Like battles first, missiles, etc").

   Whose first: the viewer's own (a search is always theirs), then each ally's in `allyIds` order —
   the seats' — and last what concerns nobody on the team (a survival wave landing). An event about
   the viewer and an ally is the viewer's. Then, inside each, the type in `TYPE_ORDER`; a type it
   does not name goes after the named ones. The sort is stable, so two lines of one type keep the
   server's order. */
const TYPE_ORDER = [
  "battle_ended",
  "battle",
  "missile_strike",
  "cell_lost",
  "cell_captured",
  "player_eliminated",
  "enemy_appeared",
  "enemy_sighted",
  "march_halted",
  "starvation",
  "upgrade_cancelled",
  "founding_cancelled",
  "tunnel_cancelled",
  "discharge_cancelled",
]

export const sortRoundEvents = (events, allyIds = []) => {
  const allies = allyIds.map(Number)
  const ownerRank = (event) => {
    if (event.concernsMe) return 0
    const subjects = (event.subjectIds || []).map(Number)
    const ally = allies.findIndex((id) => subjects.includes(id))
    return ally === -1 ? allies.length + 1 : ally + 1
  }
  const typeRank = (event) => {
    const rank = TYPE_ORDER.indexOf(event.type)
    return rank === -1 ? TYPE_ORDER.length : rank
  }
  return (events || [])
    .map((event, index) => ({ event, index, owner: ownerRank(event), type: typeRank(event) }))
    .sort((a, b) => a.owner - b.owner || a.type - b.type || a.index - b.index)
    .map(({ event }) => event)
}

/* The starvation line needs two things the display shape does not carry — what starved, unit by
   unit, and how many cells it happened across (S22a: one event per player per round, `coordinates`
   the cell that lost most, `detail.cells` the rest). They are read back off the raw events here,
   matched by position (the display list is the raw list mapped one to one) and by site as a
   fallback, so the wording can name "20 soldiers, 3 rangers" without the selector changing shape. */
export const enrichRoundEvents = (events, rawEvents) =>
  (events || []).map((event, index) => {
    if (event.type !== "starvation") return event
    const raw =
      (rawEvents || [])[index]?.type === "starvation" &&
      rawEvents[index].coordinates?.[0] === event.column &&
      rawEvents[index].coordinates?.[1] === event.row
        ? rawEvents[index]
        : (rawEvents || []).find(
            (candidate) =>
              candidate.type === "starvation" &&
              candidate.coordinates?.[0] === event.column &&
              candidate.coordinates?.[1] === event.row
          )
    if (!raw) return event
    return {
      ...event,
      starvedByUnit: raw.detail?.starved_by_unit || null,
      starvedCells: Array.isArray(raw.detail?.cells) ? raw.detail.cells.length : null,
    }
  })

/* Who a wave's newcomer is, in the three facts a player can act on: its name, the class it
   fields and how hard it plays (SV12). All three come off the seat the server added to
   `players` with the event, so none of them is a second copy of anything. The banner over the
   map and both report lines quote the same three, which is why they are built once. */
export const spawnDescription = (event) => ({
  name: event.appearedName ?? "?",
  race: raceNames[event.appearedRace] || event.appearedRace || "?",
  difficulty: event.appearedDifficulty
    ? i18n.t(`game.forces.difficulty_${event.appearedDifficulty}`, { defaultValue: event.appearedDifficulty })
    : "?",
  wave: Number(event.appearedWave ?? 0),
  // Who it came for (29/F4, owner 2026-09-20: a wave is aimed at the player with the fewest
  // enemies on them). Off the seat like the other three; the wire carries the id.
  target: event.appearedTarget ?? null,
})

/* The one sentence that names a newcomer's target, appended rather than folded into each line:
   there are four of them (the report line, the report detail, and the tour's two), and a target
   is optional — a wave spawned with nobody left to aim at has none — so one key beats eight. */
export const spawnTargetLine = (target) =>
  target ? i18n.t("game.events.enemyAppearedTarget", { target }) : null

export const withSpawnTarget = (text, target) =>
  [text, spawnTargetLine(target)].filter(Boolean).join(" ")

// "20 soldiers, 3 rangers" for the starvation line, or nothing when the event predates S22a.
const starvationUnits = (event) => {
  const detail = unitBreakdown(event.starvedByUnit)
  return detail || null
}

export const describeRoundEvent = (event) => {
  const where = place(event.column, event.row)

  if (event.type === "player_eliminated") {
    // A player out of the game (R5): the round the last crown fell, everything of theirs left the
    // map. Named for everybody — elimination is as public as ownership.
    if (event.eliminatedIsMe) return { tone: "bad", text: i18n.t("game.events.youEliminated") }
    return {
      tone: event.eliminatedIsAlly ? "bad" : "good",
      text: i18n.t("game.events.playerEliminated", { username: event.eliminatedName ?? "?" }),
    }
  }

  if (event.type === "cell_captured") {
    if (event.capturedByUs) return { tone: "good", text: i18n.t("game.events.captured", { where }) }
    if (event.lostByUs) return { tone: "bad", text: i18n.t("game.events.lost", { where }) }
    return { tone: "neutral", text: i18n.t("game.events.changedHands", { where }) }
  }

  if (event.type === "cell_lost") {
    // Q16: nobody gains a cell in the same breath any more — the deed dissolves to neutral, and
    // whoever wants the ground pays to claim it. The loser gets a line (S22b), and so does the
    // one who walked in and took it; anybody else's release is dropped by `collapseRoundEvents`.
    if (event.lostByUs) return { tone: "bad", text: i18n.t("game.events.cellLost", { where }) }
    if (event.takenByUs) return { tone: "good", text: i18n.t("game.events.cellTaken", { where }) }
    return null
  }

  if (event.type === "starvation") {
    // One line per player per round (S22a): the total, then what it was made of, and the cell
    // count when the famine reached more than one — a single site is named in the old way.
    const units = starvationUnits(event)
    if (units && event.starvedCells > 1) {
      return {
        tone: "bad",
        text: i18n.t("game.events.starvationAcross", { starved: event.starved, units, count: event.starvedCells }),
      }
    }
    if (units) {
      return { tone: "bad", text: i18n.t("game.events.starvationUnits", { starved: event.starved, units, where }) }
    }
    return {
      tone: "bad",
      text: i18n.t("game.events.starvation", { starved: event.starved, where }),
    }
  }

  if (event.type === "missile_strike") {
    // A missile lands at the start of the round after it was fired (owner, 2026-10-02), and until
    // this event existed the victim's army shrank between ticks with nothing saying why.
    if (event.firedByUs && event.spared) return { tone: "neutral", text: i18n.t("game.events.ourMissileSpared", { where }) }
    if (event.firedByUs && event.hitNobody) return { tone: "neutral", text: i18n.t("game.events.ourMissileEmpty", { where }) }
    if (event.firedByUs) {
      // A bomb does not read team colours (S21): a shot at a square where our own stacks stand
      // kills them too, and the line says so (owner, 2026-10-10, game 171: Laura's second missile
      // at 43-20 killed her 70 rangers and 3 tanks, and the report named only the zombies).
      const ours =
        event.ownLosses > 0
          ? withBreakdown(i18n.t("game.events.ourMissileOwnLosses", { losses: event.ownLosses }), event.ownLossesByUnit)
          : null
      if (ours && event.enemyLosses === 0 && !event.wallAbsorbed) {
        return {
          tone: "bad",
          text: withBreakdown(i18n.t("game.events.ourMissileOnlyOwn", { where, losses: event.ownLosses }), event.ownLossesByUnit),
        }
      }
      const line = event.wallAbsorbed
        ? i18n.t("game.events.ourMissileWall", { where })
        : withBreakdown(i18n.t("game.events.ourMissile", { where, losses: event.enemyLosses }), event.enemyLossesByUnit)
      return { tone: ours || event.wallAbsorbed ? "neutral" : "good", text: ours ? `${line} ${ours}` : line }
    }
    if (event.wallAbsorbed) return { tone: "neutral", text: i18n.t("game.events.theirMissileWall", { where }) }
    return {
      tone: "bad",
      text: withBreakdown(i18n.t("game.events.theirMissile", { where, losses: event.ownLosses }), event.ownLossesByUnit),
    }
  }

  if (event.type === "tunnel_cancelled") {
    /* A tunnel entry or exit of last round that could not be made (owner, 2026-10-05): the order
       waits for the round to turn, and the city or the units may be gone by then. */
    const way = event.tunnelDirection === "unload" ? "unload" : "load"
    const reason = ["lost", "contested", "gone"].includes(event.cancelReason) ? event.cancelReason : "other"
    return { tone: "bad", text: i18n.t(`game.events.tunnelCancelled.${way}.${reason}`, { where }) }
  }

  if (event.type === "discharge_cancelled") {
    /* A discharge of last round that was not made (owner, 2026-10-23, requirement 36 D3): the cell
       was lost, is in a fight, or the bodies are gone. Told to the orderer alone. */
    const reason = ["lost", "contested", "gone"].includes(event.cancelReason) ? event.cancelReason : "other"
    return { tone: "bad", text: i18n.t(`game.events.dischargeCancelled.${reason}`, { where }) }
  }

  if (event.type === "upgrade_cancelled" || event.type === "founding_cancelled") {
    /* An order of last round that did not land (owner, 2026-10-02: a level and a colony are
       orders, priced again and paid when the round turns). Only the player who gave it is told,
       and the server names the reason by a code. */
    const reason = ["lost", "unaffordable", "taken", "left", "contested"].includes(event.cancelReason)
      ? event.cancelReason
      : "other"
    if (event.type === "founding_cancelled") {
      return { tone: "bad", text: i18n.t(`game.events.foundingCancelled.${reason}`, { where }) }
    }
    const code = event.cancelledBuilding || ""
    const building = i18n.t(`city.buildings.${code.replace(/_([a-z])/g, (_, c) => c.toUpperCase())}`, {
      defaultValue: code,
    })
    return { tone: "bad", text: i18n.t(`game.events.upgradeCancelled.${reason}`, { where, building }) }
  }

  if (event.type === "march_halted") {
    /* Two reasons a march stands still, and they read differently: it walked into a fight it
       could not finish, or it reached one that broke out earlier in this same round and joins it
       next round. A fight carried over from an earlier round is never one a march is late to: it
       is fought after every march has moved, with all of them (owner, 2026-10-03). The server
       sends the flag, never the sentence. */
    const key = event.haltedLate ? "game.events.marchHaltedLate" : "game.events.marchHalted"
    return { tone: "bad", text: i18n.t(key, { where }) }
  }

  if (event.type === "enemy_sighted") {
    // A stack that moved this round and stands in our vision (owner, 2026-09-07). A one-round
    // strike is withheld until it lands, so this line — and the map's ring — is the first word.
    const from = event.sightedFrom ? place(event.sightedFrom.column, event.sightedFrom.row) : null
    const who = event.sightedName ?? i18n.t("game.events.theEnemy")
    return {
      tone: "bad",
      text: i18n.t(from ? "game.events.enemySightedFrom" : "game.events.enemySighted", {
        who,
        where,
        from,
        count: event.sightedUnits,
      }),
    }
  }

  if (event.type === "enemy_appeared") {
    /* A survival wave has landed (SV11/SV12). It is news for everybody — the server addresses the
       event to every player — and the line is the written half of the tour: the camera moves on,
       and this is what is left saying who arrived and how hard they play.

       Wave 0 is the board's opening, so it is worded "at the start": calling it wave 0 asks a
       player to count a wave they did not live through. */
    const who = spawnDescription(event)
    return {
      tone: "warn",
      text: withSpawnTarget(
        i18n.t(who.wave > 0 ? "game.events.enemyAppeared" : "game.events.enemyAppearedOpening", {
          ...who,
          where,
        }),
        who.target
      ),
    }
  }

  if (event.type === "battle_ended") {
    /* The engine closed this battle's books; the line opens the battle view on its final round.

       Green is a battle won and nothing else (owner, 2026-09-09: "Green events: Combat won. Red
       events: Combat fighting or lost"). A draw is not a win, so it is red with the rest — the
       colour answers "did that go my way?", and "nobody holds the field" is not a yes. */
    if (event.winnerTeam == null) {
      return { tone: "bad", text: i18n.t("game.events.battleEndedDraw", { where, count: event.rounds }) }
    }
    return {
      tone: event.wonByUs ? "good" : "bad",
      text: withMeat(
        i18n.t(event.wonByUs ? "game.events.battleEndedWon" : "game.events.battleEndedLost", {
          where,
          count: event.rounds,
        }),
        event.ownMeatWon
      ),
    }
  }

  if (event.type === "battle") {
    // Say what it cost, from this player's side.
    const parts = []
    if (event.enemyLosses) parts.push(i18n.t("game.events.battleKilled", { losses: event.enemyLosses }))
    if (event.ownLosses) parts.push(i18n.t("game.events.battleLost", { losses: event.ownLosses }))
    const summary = parts.length ? parts.join(", ") : i18n.t("game.events.battleNoCasualties")
    /* A battle still burning is red, whatever the round's exchange rate was (owner, 2026-09-09).
       It used to be scored on the kill ratio, so a fight going badly enough to lose the city read
       green for three rounds because the trade was favourable in each of them. Green is reserved
       for `battle_ended` with the field ours; while it burns, the only true thing to say is that
       it is burning. */
    const tone = "bad"
    return {
      tone,
      text: withMeat(
        i18n.t(event.wallAbsorbed ? "game.events.battleWall" : "game.events.battle", { where, summary }),
        event.ownMeatWon
      ),
    }
  }

  // A type this has no wording for. Battle used to be the fallback, so a new event on the server
  // would have been reported to the player as a battle that never happened — a worse failure than
  // saying little, and exactly the quiet mis-description requirement 16 exists to stop.
  console.warn("[round report] no wording for event type:", event.type)
  return { tone: "neutral", text: i18n.t("game.events.unknown", { where }) }
}

export default describeRoundEvent

/* The same events, told at length.

   `describeRoundEvent` writes one line for the dock panel, where space is the constraint. The
   round digest has a card per event and can afford a headline and a sentence explaining what it
   means for the next round — which is the thing a player actually has to decide about. The wording
   above stays the single source for the short form; nothing is duplicated between them. */
/* `team` is the viewer's side, and it is only ever used for a glyph: starvation is about food,
   and food is 🌽 or 🥩 depending on who is going hungry. That is the side of the seat that
   starved (`subjectTeam`, off the event), and the viewer's only when the event names nobody: a
   spectator reads every seat's events, and a horde's famine drew the watched human's corn. */
export const detailRoundEvent = (event, team) => {
  const where = place(event.column, event.row)
  const short = describeRoundEvent(event)
  if (!short) return null
  const { tone } = short

  if (event.type === "player_eliminated") {
    return {
      tone,
      icon: "☠",
      title: event.eliminatedIsMe
        ? i18n.t("game.eventDetails.youEliminatedTitle")
        : i18n.t("game.eventDetails.playerEliminatedTitle", { username: event.eliminatedName ?? "?" }),
      detail: event.eliminatedIsMe
        ? i18n.t("game.eventDetails.youEliminatedDetail")
        : i18n.t("game.eventDetails.playerEliminatedDetail"),
    }
  }

  if (event.type === "cell_captured") {
    if (event.capturedByUs) {
      return {
        tone: "good",
        icon: "🚩",
        title: i18n.t("game.eventDetails.capturedTitle", { where }),
        detail: i18n.t("game.eventDetails.capturedDetail"),
      }
    }
    if (event.lostByUs) {
      return {
        tone: "bad",
        icon: "🚩",
        title: i18n.t("game.eventDetails.lostTitle", { where }),
        detail: i18n.t("game.eventDetails.lostDetail"),
      }
    }
    return { tone: "neutral", icon: "🚩", title: i18n.t("game.eventDetails.changedHandsTitle", { where }), detail: "" }
  }

  if (event.type === "cell_lost" && event.takenByUs && !event.lostByUs) {
    return {
      tone: "good",
      icon: "🚩",
      title: i18n.t("game.eventDetails.cellTakenTitle", { where }),
      detail: i18n.t("game.eventDetails.cellTakenDetail"),
    }
  }

  if (event.type === "cell_lost") {
    return {
      tone: "bad",
      icon: "🏳",
      title: i18n.t("game.eventDetails.cellLostTitle", { where }),
      detail: i18n.t("game.eventDetails.cellLostDetail"),
    }
  }

  if (event.type === "battle") {
    return {
      tone,
      icon: "⚔",
      title: i18n.t("game.eventDetails.battleTitle", { where }),
      detail: withMeat(
        i18n.t(event.wallAbsorbed ? "game.eventDetails.battleWallDetail" : "game.eventDetails.battleDetail", {
          killed: event.enemyLosses,
          lost: event.ownLosses,
        }),
        event.ownMeatWon
      ),
      bar: { good: event.enemyLosses, bad: event.ownLosses },
    }
  }

  if (event.type === "starvation") {
    const units = starvationUnits(event)
    return {
      tone: "warn",
      icon: foodIcon(event.subjectTeam || team),
      title:
        event.starvedCells > 1
          ? i18n.t("game.eventDetails.starvationAcrossTitle", { starved: event.starved, count: event.starvedCells })
          : i18n.t("game.eventDetails.starvationTitle", { starved: event.starved, where }),
      detail: units
        ? `${i18n.t("game.eventDetails.starvationUnits", { units })} ${i18n.t("game.eventDetails.starvationDetail")}`
        : i18n.t("game.eventDetails.starvationDetail"),
    }
  }

  if (event.type === "missile_strike") {
    return {
      tone,
      icon: "🚀",
      title: i18n.t(event.firedByUs ? "game.eventDetails.ourMissileTitle" : "game.eventDetails.theirMissileTitle", {
        where,
      }),
      detail: event.hitNobody
        ? i18n.t("game.eventDetails.missileEmptyDetail")
        : event.wallAbsorbed
        ? i18n.t("game.eventDetails.missileWallDetail")
        : event.firedByUs && event.enemyLosses === 0 && event.ownLosses > 0
        ? withBreakdown(i18n.t("game.eventDetails.theirMissileDetail", { losses: event.ownLosses }), event.ownLossesByUnit)
        : event.firedByUs
        ? [
            withBreakdown(i18n.t("game.eventDetails.ourMissileDetail", { losses: event.enemyLosses }), event.enemyLossesByUnit),
            event.ownLosses > 0
              ? withBreakdown(i18n.t("game.eventDetails.ourMissileOwnDetail", { losses: event.ownLosses }), event.ownLossesByUnit)
              : null,
          ]
            .filter(Boolean)
            .join(" ")
        : withBreakdown(i18n.t("game.eventDetails.theirMissileDetail", { losses: event.ownLosses }), event.ownLossesByUnit),
    }
  }

  if (event.type === "tunnel_cancelled") {
    /* A tunnel entry or exit of last round that could not be made (owner, 2026-10-05): the order
       waits for the round to turn, and the city or the units may be gone by then. */
    const way = event.tunnelDirection === "unload" ? "unload" : "load"
    const reason = ["lost", "contested", "gone"].includes(event.cancelReason) ? event.cancelReason : "other"
    return { tone: "bad", text: i18n.t(`game.events.tunnelCancelled.${way}.${reason}`, { where }) }
  }

  if (event.type === "discharge_cancelled") {
    /* A discharge of last round that was not made (owner, 2026-10-23, requirement 36 D3): the cell
       was lost, is in a fight, or the bodies are gone. Told to the orderer alone. */
    const reason = ["lost", "contested", "gone"].includes(event.cancelReason) ? event.cancelReason : "other"
    return { tone: "bad", text: i18n.t(`game.events.dischargeCancelled.${reason}`, { where }) }
  }

  if (event.type === "upgrade_cancelled" || event.type === "founding_cancelled") {
    /* An order of last round that did not land (owner, 2026-10-02: a level and a colony are
       orders, priced again and paid when the round turns). Only the player who gave it is told,
       and the server names the reason by a code. */
    const reason = ["lost", "unaffordable", "taken", "left", "contested"].includes(event.cancelReason)
      ? event.cancelReason
      : "other"
    if (event.type === "founding_cancelled") {
      return { tone: "bad", text: i18n.t(`game.events.foundingCancelled.${reason}`, { where }) }
    }
    const code = event.cancelledBuilding || ""
    const building = i18n.t(`city.buildings.${code.replace(/_([a-z])/g, (_, c) => c.toUpperCase())}`, {
      defaultValue: code,
    })
    return { tone: "bad", text: i18n.t(`game.events.upgradeCancelled.${reason}`, { where, building }) }
  }

  if (event.type === "march_halted") {
    return {
      tone: "warn",
      icon: "⛔",
      title: i18n.t("game.eventDetails.marchHaltedTitle", { where }),
      detail: i18n.t(
        event.haltedLate ? "game.eventDetails.marchHaltedLateDetail" : "game.eventDetails.marchHaltedDetail"
      ),
    }
  }

  if (event.type === "enemy_appeared") {
    const who = spawnDescription(event)
    return {
      tone: "warn",
      // Its own glyph, and deliberately not the sighting's ⚠: a march spotted in your ring and a
      // whole new enemy landing on the map are not the same news, and the digest is a column of
      // icons before it is a column of sentences.
      icon: "☣",
      title: i18n.t("game.eventDetails.enemyAppearedTitle", { where }),
      detail: withSpawnTarget(
        i18n.t(
          who.wave > 0
            ? "game.eventDetails.enemyAppearedDetail"
            : "game.eventDetails.enemyAppearedOpeningDetail",
          { ...who, cities: Number(event.appearedCities ?? 0) }
        ),
        who.target
      ),
    }
  }

  if (event.type === "enemy_sighted") {
    const from = event.sightedFrom ? place(event.sightedFrom.column, event.sightedFrom.row) : null
    return {
      tone: "warn",
      icon: "⚠",
      title: i18n.t("game.eventDetails.enemySightedTitle", { where, count: event.sightedUnits }),
      detail: [
        from ? i18n.t("game.eventDetails.enemySightedFrom", { from }) : null,
        i18n.t(event.sightedFighting ? "game.eventDetails.enemySightedFighting" : "game.eventDetails.enemySightedStanding"),
      ]
        .filter(Boolean)
        .join(" "),
    }
  }

  return { tone, icon: "•", title: short.text, detail: "" }
}
